import React, { useState } from 'react';
import { useLanguage } from "../../contexts/LanguageContext";
import { useNavigate,Link } from 'react-router-dom';
import { postApi } from '../../utils/api/api';
import importImage from "../../utils/images/importImage";
import { showToast } from '../../utils/toast';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const bgImg = importImage("contact-bg.jpg");

const RegisterForm = () => {
  const RegisterFormImage = importImage("register.png");
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    c_name: '',
    c_type: '',
    c_website: '',
    c_address: '',
    email: '',
    phone_number: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handlePhoneChange = (value, countryData) => {
    const countryCode = countryData.dialCode;
    const phoneNumberWithoutPlus = value.startsWith('+') ? value.slice(1) : value;
    const fullNumber = phoneNumberWithoutPlus.startsWith(countryCode)
      ? phoneNumberWithoutPlus
      : `${countryCode}${phoneNumberWithoutPlus}`;
    setFormData({ ...formData, phone_number: fullNumber });
    setErrors((prev) => ({ ...prev, phone_number: '' }));
  };

  const validateForm = () => {
    let formErrors = {};
    let isValid = true;

    if (!formData.name) {
      formErrors.name = t('errors.nameRequired');
      isValid = false;
    }

    if (!formData.c_name) {
      formErrors.c_name = t('errors.companyNameRequired');
      isValid = false;
    }

    if (!formData.c_type) {
      formErrors.c_type = t('errors.companyTypeRequired');
      isValid = false;
    }

    if (!formData.c_address) {
      formErrors.c_address = t('errors.companyAddressRequired');
      isValid = false;
    }

    if (!formData.email) {
      formErrors.email = t('errors.emailRequired');
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      formErrors.email = t('errors.invalidEmail');
      isValid = false;
    }

    if (!formData.phone_number) {
      formErrors.phone_number = t('errors.phoneRequired');
      isValid = false;
    }

    setErrors(formErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const message = await postApi('/user-register', formData);
      showToast('success', message.message || t('register.success', 'Registration successful!'));
      setTimeout(() => {
        navigate('/login');
      }, 5000);
    } catch (err) {
      const apiErrors = err.response?.data?.data || {};
      const formattedErrors = {};
      for (const [field, messages] of Object.entries(apiErrors)) {
        formattedErrors[field] = messages.join(', ');
      }
      setErrors((prevErrors) => ({ ...prevErrors, ...formattedErrors }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-page bg-light min-vh-100 d-flex align-items-center py-5"
      style={{
        backgroundImage: `url(${bgImg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
      
    >
      <div className="container">
        <div className="row g-0">
          {/* Combined Card - Form + Image */}
          <div className="col-12 col-lg-12">
            <div className="card shadow-lg border-0 overflow-hidden" style={{ 
              borderRadius: '20px',
              border: '1px solid rgba(0,0,0,0.1)',
              boxShadow: '0 15px 30px rgba(0,0,0,0.12)'
            }}>
              <div className="row g-0 h-100">
                {/* Form Column */}
                <div className="col-lg-7 d-flex align-items-center">
                  <div className="card-body p-4 p-md-5">
                    <div className="text-center mb-4">
                      <h2 className="text-primary fw-bold">
                        {t('register.heading', 'Create Account')}
                      </h2>
                      <p className="text-muted">
                        {t('register.subheading', 'Join our platform today')}
                      </p>
                    </div>

                    <form onSubmit={handleSubmit} className="needs-validation" noValidate>
                      <div className="row">
                        <div className="col-lg-12 mb-4">
                          <label htmlFor="name" className="form-label fw-semibold">
                            {t('register.nameLabel', 'Full Name')}
                            <span className="text-danger">*</span>
                          </label>
                          <div className="input-group has-validation">
                            <span className="input-group-text bg-light border-end-0">
                              <i className="bi bi-person text-muted"></i>
                            </span>
                            <input
                              id="name"
                              type="text"
                              name="name"
                              className={`form-control py-3 ${errors.name ? 'is-invalid' : ''}`}
                              placeholder={t('placeholders.name', 'Your full name')}
                              value={formData.name}
                              onChange={handleChange}
                              maxLength={245}
                              required
                            />
                            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                          </div>
                        </div>

                        <div className="col-lg-6 mb-4">
                          <label htmlFor="c_name" className="form-label fw-semibold">
                            {t('register.companyNameLabel', 'Company Name')}
                            <span className="text-danger">*</span>
                          </label>
                          <div className="input-group has-validation">
                            <span className="input-group-text bg-light border-end-0">
                              <i className="bi bi-building text-muted"></i>
                            </span>
                            <input
                              id="c_name"
                              type="text"
                              name="c_name"
                              className={`form-control py-3 ${errors.c_name ? 'is-invalid' : ''}`}
                              placeholder={t('placeholders.companyName', 'Your company name')}
                              value={formData.c_name}
                              onChange={handleChange}
                              maxLength={245}
                              required
                            />
                            {errors.c_name && <div className="invalid-feedback">{errors.c_name}</div>}
                          </div>
                        </div>

                        <div className="col-lg-6 mb-4">
                          <label htmlFor="c_type" className="form-label fw-semibold">
                            {t('register.companyTypeLabel', 'Company Type')}
                            <span className="text-danger">*</span>
                          </label>
                          <div className="input-group has-validation">
                            <span className="input-group-text bg-light border-end-0">
                              <i className="bi bi-diagram-3 text-muted"></i>
                            </span>
                            <select
                              id="c_type"
                              name="c_type"
                              className={`form-control py-3 ${errors.c_type ? 'is-invalid' : ''}`}
                              value={formData.c_type}
                              onChange={handleChange}
                              required
                            >
                              <option value="">{t('register.selectCompanyType', 'Select Company Type')}</option>
                              <option value="Spinning">{t('register.companyTypeSpinning', 'Spinning')}</option>
                              <option value="Knitting">{t('register.companyTypeKnitting', 'Knitting')}</option>
                              <option value="Weaving">{t('register.companyTypeWeaving', 'Weaving')}</option>
                              <option value="Warp knitting">{t('register.companyTypeWarpKnitting', 'Warp knitting')}</option>
                              <option value="Wholesaler">{t('register.companyTypeWholesaler', 'Wholesaler')}</option>
                              <option value="Other">{t('register.companyTypeOther', 'Other')}</option>
                            </select>
                            {errors.c_type && <div className="invalid-feedback">{errors.c_type}</div>}
                          </div>
                        </div>

                        <div className="col-lg-6 mb-4">
                          <label htmlFor="c_website" className="form-label fw-semibold">
                            {t('register.companyWebsiteLabel', 'Company Website')}
                          </label>
                          <div className="input-group has-validation">
                            <span className="input-group-text bg-light border-end-0">
                              <i className="bi bi-globe text-muted"></i>
                            </span>
                            <input
                              id="c_website"
                              type="text"
                              name="c_website"
                              className="form-control py-3"
                              placeholder={t('placeholders.companyWebsite', 'https://example.com')}
                              value={formData.c_website}
                              onChange={handleChange}
                              maxLength={245}
                            />
                          </div>
                        </div>

                        <div className="col-lg-6 mb-4">
                          <label htmlFor="c_address" className="form-label fw-semibold">
                            {t('register.companyAddressLabel', 'Company Address')}
                            <span className="text-danger">*</span>
                          </label>
                          <div className="input-group has-validation">
                            <span className="input-group-text bg-light border-end-0">
                              <i className="bi bi-geo-alt text-muted"></i>
                            </span>
                            <input
                              id="c_address"
                              type="text"
                              name="c_address"
                              className={`form-control py-3 ${errors.c_address ? 'is-invalid' : ''}`}
                              placeholder={t('placeholders.companyAddress', 'Company address')}
                              value={formData.c_address}
                              onChange={handleChange}
                              maxLength={245}
                              required
                            />
                            {errors.c_address && <div className="invalid-feedback">{errors.c_address}</div>}
                          </div>
                        </div>

                        <div className="col-lg-6 mb-4">
                          <label htmlFor="email" className="form-label fw-semibold">
                            {t('register.emailLabel', 'Email')}
                            <span className="text-danger">*</span>
                          </label>
                          <div className="input-group has-validation">
                            <span className="input-group-text bg-light border-end-0">
                              <i className="bi bi-envelope text-muted"></i>
                            </span>
                            <input
                              id="email"
                              type="email"
                              name="email"
                              className={`form-control py-3 ${errors.email ? 'is-invalid' : ''}`}
                              placeholder={t('placeholders.email', 'your@email.com')}
                              value={formData.email}
                              onChange={handleChange}
                              maxLength={245}
                              required
                            />
                            {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                          </div>
                        </div>

                        <div className="col-lg-6 mb-4">
                          <label htmlFor="phone_number" className="form-label fw-semibold">
                            {t('register.phoneLabel', 'Phone Number')}
                            <span className="text-danger">*</span>
                          </label>
                          <div className="input-group has-validation">
                            {/* <span className="input-group-text bg-light border-end-0">
                              <i className="bi bi-phone text-muted"></i>
                            </span> */}
                            <PhoneInput
                              inputProps={{
                                id: "phone_number",
                                name: "phone_number",
                                className: `form-control py-3 ${errors.phone_number ? 'is-invalid' : ''}`,
                                required: true
                              }}
                              country={'id'}
                              value={formData.phone_number}
                              onChange={handlePhoneChange}
                              inputClass={`w-100 ${errors.phone_number ? 'is-invalid' : ''}`}
                              containerClass="w-100"
                            />
                            {errors.phone_number && <div className="invalid-feedback">{errors.phone_number}</div>}
                          </div>
                        </div>

                        <div className="col-12 mt-3">
                          <div className="d-grid">
                            <button
                              type="submit"
                              className="btn btn-primary py-3 fw-semibold"
                              disabled={loading}
                            >
                              {loading ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                  {t('buttons.registering', 'Registering...')}
                                </>
                              ) : (
                                t('buttons.register', 'Register Now')
                              )}
                            </button>
                          </div>
                        </div>

                        <div className="col-12 text-center mt-4">
                          <p className="mb-0">
                            {t('register.alreadyHaveAccount', 'Already have an account?')}{' '}
                            <Link to="/login" className="text-primary fw-semibold text-decoration-none">
                              {t('register.loginHere', 'Login here')}
                            </Link>
                          </p>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>

                {/* Image Column */}
                <div className="col-lg-5 d-none d-lg-flex position-relative">
                  <div className="h-100 w-100" style={{
                    backgroundImage: `url(${RegisterFormImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    borderLeft: '1px solid rgba(0,0,0,0.1)',
                    borderTopRightRadius: '20px',
                    borderBottomRightRadius: '20px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <div className="position-absolute top-0 start-0 w-100 h-100" 
                      style={{
                      // background: 'linear-gradient(135deg, rgba(13,110,253,0.15) 0%, rgba(255,255,255,0) 100%)'
                    }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;